import React, { FC, memo, useContext } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { SearchBar, UserContextConsumer } from "@ipd/js-common";
import { AppHeader } from "@ipd/app-header";
import { DrugSearch, TherapeuticAreaSearch } from "@ipd/search-components";
import { RegionMegaMenu } from "@ipd/region-mega-menu";

import "./Layout.css";
import config from "../../config";
import { TherapeuticAreaContext } from "../../context";
import ErrorBoundary from "../Overlays/ErrorBoundary";
import FooterComponent from "./Footer";

const useStyles = makeStyles({
  app: {
    display: "flex",
    flexDirection: "column",
    width: 1170,
    minHeight: "100vh",
    margin: "0 auto",
    backgroundColor: "white",
  },
  overlay: {
    display: "flex",
    height: 115,
    paddingTop: 70,
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    height: "100%",
    // width: "95%",
    maxWidth: 550,
    "& .MuiOutlinedInput-adornedStart": {
      paddingLeft: 6,
    },
    "& .MuiOutlinedInput-adornedEnd": {
      paddingRight: 6,
    },
    paddingLeft: 6,
  },
  regionMegaMenuContainer: {
    maxWidth: 48,
    marginLeft: -10,
  },
});

type AppLayoutProps = {
  region?: string;
};

const searchBarContainerStyle = {
  color: "initial",
  height: 69,
};

const searchBarChildrenStyle = {
  alignSelf: "center",
  display: "inline-flex",
  justifyContent: "space-between",
  height: "100%",
  maxWidth: 550,
};

const AppLayout: FC<AppLayoutProps> = memo(({ region, children }) => {
  const classes = useStyles();

  const { state } = useLocation();
  const [therapeuticAreaContext] = useContext(TherapeuticAreaContext);
  const { activeCountry = "us" } = (state ?? {}) as Record<string, string>;
  const regionCode = { us: "USA", eu: "EP" }[region ?? activeCountry] ?? (region ?? activeCountry).toUpperCase();
  const currentApp = therapeuticAreaContext ? "TherapeuticAreas" : "LifeCycle";

  return (
    <ErrorBoundary>
      <div className={classes.app}>
        <main>
          <UserContextConsumer>
            {(user): JSX.Element | null => {
              if (!user.isLoggedIn) return null;
              return (
                <>
                  <AppHeader stage={config.stage} currentApp={currentApp} />
                  <SearchBar
                    logoSource="/images/lci-white.png"
                    logoStyle={{ height: 46, marginRight: 6 }}
                    logoContainerStyle={{ minWidth: 240, maxWidth: 240, float: "right" }}
                    buttonsContainerStyle={{ paddingLeft: 90, width: 370 }}
                    onLogoClick={() => {
                      window.location.href = "https://secure.ipdanalytics.com/User/Pharma/Home/Insights";
                    }}
                    searchBarContainerStyle={searchBarContainerStyle}
                    childrenContainerStyle={searchBarChildrenStyle}
                  >
                    <div className={classes.searchContainer}>
                      <div className={classes.regionMegaMenuContainer}>
                        <RegionMegaMenu
                          user={user}
                          featureToggles={config.featureToggles}
                          horizontalOffset={-7}
                          verticalOffset={8}
                          buttonColor="#FFFFFF"
                        />
                      </div>
                      <a
                        href="https://secure.ipdanalytics.com/User/Pharma/Favorite"
                        style={{ zIndex: 1, marginTop: 2, marginLeft: 8 }}
                      >
                        <img src="/images/NewFavoritesIcon2.png" style={{ height: 30 }} />
                      </a>
                      <Box style={{ flex: 1, marginRight: "8px" }}>
                        {!therapeuticAreaContext && (
                          <DrugSearch
                            regionCode={regionCode}
                            rootStyle={{ minWidth: 538, marginLeft: 12 }}
                            userAppUrl={config.USER_URL}
                          />
                        )}
                        {therapeuticAreaContext && (
                          <TherapeuticAreaSearch taSlug={therapeuticAreaContext} ripdUrl={config.IPD_URL} />
                        )}
                      </Box>
                    </div>
                  </SearchBar>
                  {children}
                  <FooterComponent />
                </>
              );
            }}
          </UserContextConsumer>
        </main>
      </div>
    </ErrorBoundary>
  );
});

export default AppLayout;
